import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaLeaf, FaUserTie, FaBalanceScale, FaHandshake, 
  FaFlask, FaIndustry, FaShieldAlt, FaBookmark
} from 'react-icons/fa';
import './css/WhyNimble.css';
import SEO from '../components/SEO';

// Import images
import heroImage from '../assets/images/Nimble Green Chemistry -2.jpg';
import labImage from '../assets/images/Laboratroy Image .jpg';
import greenChemImage from '../assets/images/Nimble Green Chemistry -2.jpg';

const WhyNimble = () => {
  return (
    <>
      <SEO
        title="Why Choose Nimble Group - Expertise in Sustainable Chemical Solutions"
        description="Discover why industry leaders choose Nimble Group for eco-friendly chemical solutions. 25+ years of expertise, green chemistry principles, and sustainable innovation in chemical manufacturing."
        keywords="why choose nimble, chemical expertise, sustainable solutions, green chemistry, environmental responsibility, chemical innovation, eco-friendly manufacturing, Manufacturers of eco solutions, Manufacturers of eco friendly products, Manufacturers of eco friendly chemicals, Manufacturers of eco friendly degreasers,Manufacturers of eco friendly alternatives,Manufacturers of eco friendly chemicals substitutes, Manufacturers of eco friendly parts washer,Manufacturers of eco friendly ultrasonic cleaners, Manufacturers of eco friendly degreasers, Sustainable chemicals, EHS approved cleaners, EPA approved cleaners, OSHA approved cleaners, OSHA audit approved cleaners, Nimble eco solutions, Nimble Eco products, Nimble chemicals, Nimble Technologies, Nimble Go Green, Nimble go green, Nimble EHS solutions,Nimble Sustainable chemicals, Nimble Go green solutions, Customized cleaners, Green cleaners, Green cleaning process, Nimble eco solutions,      "
        canonical="/expertise"
      />
      <WhyNimbleContent />
    </>
  );
};

const WhyNimbleContent = () => {
  // Animation images for the vertical tracks
  const leftTrackImages = [
    { src: require('../assets/images/5.png'), alt: 'Left Image 1' },
    { src: require('../assets/images/6.png'), alt: 'Left Image 2' },
    { src: require('../assets/images/7.png'), alt: 'Left Image 3' },
    { src: require('../assets/images/8.png'), alt: 'Left Image 4' }
  ];

  const rightTrackImages = [
    { src: require('../assets/images/1.png'), alt: 'Right Image 1' },
    { src: require('../assets/images/2.png'), alt: 'Right Image 2' },
    { src: require('../assets/images/3.png'), alt: 'Right Image 3' },
    { src: require('../assets/images/4.png'), alt: 'Right Image 4' }
  ];

  // Equal-speed, spaced vertical marquee config
  const ITEM_HEIGHT = 120; // px; matches .track-image height (7.5rem)
  const GAP = 40; // px; space between cards
  const PITCH = ITEM_HEIGHT + GAP; // distance from one card to the next
  const LOOP_DISTANCE = PITCH * leftTrackImages.length; // height of one full sequence

  const differentiators = [
    {
      icon: FaLeaf,
      title: "Eco-friendly Sustainable Solutions",
      description: "We don't follow trends — we set them. Every formulation we offer is rooted in the globally accepted principles of green chemistry, ensuring reduced environmental impact, improved operator safety, and responsible lifecycle management."
    },
    {
      icon: FaUserTie,
      title: "Technocrat-Led Custom Solutions",
      description: "Unlike traditional chemical suppliers, we are technocrats first. Our team's deep application knowledge enables us to engineer tailor-made, customer-specific solutions that address unique operational challenges, compliance needs, and sustainability goals."
    },
    {
      icon: FaBalanceScale,
      title: "Focused on EHS & Process Optimization",
      description: "We design solutions that elevate not just EHS standards, but also process performance and cost-efficiency — delivering sustainable alternatives without compromising on operational results."
    },
    {
      icon: FaHandshake,
      title: "Industry-Trusted and Globally Connected",
      description: "With 14 OEM approvals, 21 global NDAs, and 4 patented eco-products, we've earned the trust of respected manufacturers across automotive, engineering, electronics, and industrial segments."
    }
  ];

  const expertiseAreas = [
    {
      icon: FaFlask,
      title: "Aromatic Chemicals & Natural Esters",
      description: "Precision-manufactured fragrance and pharmaceutical intermediates rooted in nature.",
      image: labImage
    },
    {
      icon: FaLeaf,
      title: "Eco-Friendly Industrial Solutions",
      description: "Tailor-made, non-toxic, sustainable formulations replacing traditional hazardous chemicals.",
      image: greenChemImage
    },
    {
      icon: FaIndustry,
      title: "Advanced Heat Dissipation Compounds",
      description: "Next-generation thermal management solutions for high-performance electronics and EV applications.",
      image: labImage
    },
    {
      icon: FaShieldAlt,
      title: "Green Metalworking Additives",
      description: "High-performance, bio-based additives for lubricants and cutting fluids, enabling safer, greener manufacturing.",
      image: greenChemImage
    }
  ];

  return (
    <div className="why-nimble-page">
      {/* Hero Section */}
      <section className="hero-section" style={{ backgroundImage: `url(${heroImage})` }}>
        {/* Animated Vertical Track - Left Side (Top to Bottom) */}
        <div className="vertical-track vertical-track-left">
          <div className="track-container">
            <motion.div
              className="track-inner"
              initial={{ y: -LOOP_DISTANCE }}
              animate={{ y: 0 }}
              transition={{ duration: 6, ease: "linear", repeat: Infinity }}
            >
              {[...leftTrackImages, ...leftTrackImages].map((image, index) => (
                <div key={index} className="track-image">
                  <img src={image.src} alt={image.alt} />
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Animated Vertical Track - Right Side (Bottom to Top) */}
        <div className="vertical-track vertical-track-right">
          <div className="track-container">
            <motion.div
              className="track-inner"
              initial={{ y: 0 }}
              animate={{ y: -LOOP_DISTANCE }}
              transition={{ duration: 6, ease: "linear", repeat: Infinity }}
            >
              {[...rightTrackImages, ...rightTrackImages].map((image, index) => (
                <div key={`right-${index}`} className="track-image">
                  <img src={image.src} alt={image.alt} />
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        <div className="hero-overlay">
          <div className="hero-content">
            <h1>Why Nimble</h1>
            <h2>Trusted Technocrats, Proven Innovators</h2>
            <p>
              At Nimble Technologies, we are not just chemical manufacturers — we are technocrats driven
              by a vision to create eco-intelligent solutions that elevate EHS standards & sustainability
              standards to protect the planet.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stat-card">
          <span className="stat-number">30+</span>
          <span className="stat-label">Years of Innovation</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">14</span>
          <span className="stat-label">OEM Approvals</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">21</span>
          <span className="stat-label">Global NDAs</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">4</span>
          <span className="stat-label">Patented Eco-Products</span>
        </div>
      </section>

      {/* What Sets Us Apart Section */}
      <section className="differentiators-section">
        <h2>What Sets Us Apart</h2>
        <div className="differentiators-grid">
          {differentiators.map((item, index) => (
            <motion.div 
              key={index}
              className="differentiator-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="icon-wrapper">
                <item.icon />
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Expertise Section */}
      <section className="expertise-section">
        <h2>Our Expertise</h2>
        <p className="expertise-intro">
          At Nimble, our expertise lies in combining scientific rigor, engineering excellence, and
          responsible chemistry to deliver value-driven, eco-friendly solutions across industrial sectors.
        </p>
        <div className="expertise-grid">
          {expertiseAreas.map((area, index) => (
            <motion.div 
              key={index}
              className="expertise-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={{ backgroundImage: `url(${area.image})` }}
            >
              <div className="expertise-overlay">
                <div className="expertise-content">
                  <div className="icon-wrapper">
                    <area.icon />
                  </div>
                  <h3>{area.title}</h3>
                  <p>{area.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Leadership Philosophy Section */}
      <section className="philosophy-section" style={{ backgroundImage: `url(${labImage})` }}>
        <div className="philosophy-overlay">
          <div className="philosophy-content">
            <h2>Leadership Philosophy</h2>
            <blockquote>
              "The true success of any enterprise is not what it takes from the world, but what it gives back."
            </blockquote>
            <div className="commitment-list">
              <h3><FaBookmark style={{ marginRight: '10px' }} />We are deeply committed to:</h3>
              <ul>
                <li>Creating solutions that serve industries without harming ecosystems</li>
                <li>Respecting every stakeholder — from nature to machinery</li>
                <li>Leading with empathy, honesty, and engineering excellence</li>
                <li>Promoting conscious capitalism where business growth benefits society</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Connect with our Technical Experts</h2>
          <p>
            Explore how we can support your green manufacturing journey and process transformation.
            Let's co-create a safer, cleaner, and more responsible industrial future.
          </p>
          <button className="cta-button">Contact Us</button>
        </div>
      </section>
    </div>
  );
};

export default WhyNimble; 